import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  CircleStackIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { TraceEvent } from "../../types/aiChatClientV5";

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

export type TraceKind =
  | "reasoning"
  | "status"
  | "tool_call"
  | "sql_exec"
  | "tool_result"
  | "error"
  | "final"
  | "request_id"
  | "other";

export function classifyTraceEvent(ev: TraceEvent): TraceKind {
  const d = ev?.data ?? {};
  if (ev.event === "request-id" || d.requestId) return "request_id";
  // Tool call: has an input + a tool name
  if (d.input !== undefined && d.name !== undefined) return "tool_call";
  // SQL / tool execution details (QueryID, rows, state…)
  if (
    d.details &&
    (d.details.QueryID !== undefined ||
      d.details.state !== undefined ||
      d.details.produced_rows !== undefined)
  ) {
    return "sql_exec";
  }
  if (Array.isArray(d.content)) {
    const first = d.content[0] || {};
    if (first.type === "text") return "final";
    if (first.json?.error) return "error";
    return "tool_result";
  }
  if (d.status && d.message) return "status";
  if (d.text !== undefined) return "reasoning";
  return "other";
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const prettyJSON = (value: any): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const CopyButton: React.FC<{ getText: () => string; label?: string }> = ({
  getText,
  label = "Copy",
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(getText());
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
      title={label}
    >
      {copied ? (
        <CheckIcon className="h-3 w-3 text-green-600" />
      ) : (
        <ClipboardDocumentIcon className="h-3 w-3" />
      )}
      {copied ? "Copied" : label}
    </button>
  );
};

const SectionShell: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string; // tailwind border-l color class
  seq?: number;
  children: React.ReactNode;
}> = ({ icon, title, accent, seq, children }) => (
  <div className={`border-l-4 ${accent} bg-white rounded-md shadow-sm p-3`}>
    <div className="flex items-center gap-2 mb-1">
      <span className="flex-shrink-0">{icon}</span>
      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        {title}
      </span>
      {typeof seq === "number" && (
        <span className="ml-auto text-[10px] text-gray-400">#{seq}</span>
      )}
    </div>
    {children}
  </div>
);

// Render a Snowflake-style result_set as a compact table when possible.
const ResultSetView: React.FC<{ resultSet: any }> = ({ resultSet }) => {
  const rows: any[] = Array.isArray(resultSet?.data) ? resultSet.data : [];
  const columns: string[] =
    resultSet?.resultSetMetaData?.rowType?.map((c: any) => c.name) || [];
  const MAX = 20;

  if (rows.length === 0) {
    return (
      <pre className="text-[11px] bg-gray-50 p-2 rounded overflow-x-auto text-gray-600 max-h-48">
        <code>{prettyJSON(resultSet)}</code>
      </pre>
    );
  }

  return (
    <div>
      <div className="text-[11px] text-gray-500 mb-1">
        {rows.length} row{rows.length > 1 ? "s" : ""}
        {rows.length > MAX ? ` (showing first ${MAX})` : ""}
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-[11px]">
          {columns.length > 0 && (
            <thead className="bg-gray-100">
              <tr>
                {columns.map((c, i) => (
                  <th
                    key={i}
                    className="px-2 py-1 text-left font-semibold text-gray-600 whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.slice(0, MAX).map((row, ri) => {
              const cells = Array.isArray(row)
                ? row
                : columns.length > 0
                  ? columns.map((c) => row?.[c])
                  : Object.values(row || {});
              return (
                <tr key={ri} className={ri % 2 ? "bg-gray-50" : "bg-white"}>
                  {cells.map((cell: any, ci: number) => (
                    <td
                      key={ci}
                      className="px-2 py-1 text-gray-700 whitespace-nowrap max-w-[220px] truncate"
                      title={typeof cell === "object" ? prettyJSON(cell) : String(cell ?? "")}
                    >
                      {typeof cell === "object" ? prettyJSON(cell) : String(cell ?? "")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-event renderers
// ---------------------------------------------------------------------------

const sqlStateStyle = (state?: string, status?: string) => {
  const s = `${state || ""} ${status || ""}`.toUpperCase();
  if (s.includes("FAIL") || s.includes("ERROR"))
    return "bg-red-100 text-red-700 border-red-300";
  if (s.includes("SUCC")) return "bg-green-100 text-green-700 border-green-300";
  if (s.includes("RUN") || s.includes("COMPIL"))
    return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-gray-100 text-gray-700 border-gray-300";
};

const TraceEntry: React.FC<{ ev: TraceEvent; kind: TraceKind }> = ({
  ev,
  kind,
}) => {
  const d = ev.data ?? {};

  switch (kind) {
    case "status": {
      const isWarn = /fail|error/i.test(`${d.status}`);
      return (
        <SectionShell
          icon={
            <span
              className={`h-2 w-2 rounded-full inline-block ${
                isWarn ? "bg-red-500" : "bg-[#0f3557]"
              }`}
            />
          }
          title={d.status || "Step"}
          accent={isWarn ? "border-l-red-400" : "border-l-[#0f3557]"}
          seq={ev.seq}
        >
          <div className="text-sm text-gray-700">{d.message}</div>
          {(d.tool_type || d.details?.QueryID) && (
            <div className="mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-3">
              {d.tool_type && <span>tool: {d.tool_type}</span>}
              {d.details?.QueryID && <span>QueryID: {d.details.QueryID}</span>}
            </div>
          )}
        </SectionShell>
      );
    }

    case "tool_call": {
      const sql: string | undefined = d.input?.sql;
      return (
        <SectionShell
          icon={<WrenchScrewdriverIcon className="h-4 w-4 text-indigo-600" />}
          title={`Tool call — ${d.name}`}
          accent="border-l-indigo-500"
          seq={ev.seq}
        >
          <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-3 mb-1">
            {d.input?.semantic_model && (
              <span>model: {d.input.semantic_model}</span>
            )}
            {d.input?.error_correction && (
              <span className="text-amber-600">
                error correction: {String(d.input.error_correction)}
              </span>
            )}
            {d.client_side_execute !== undefined && (
              <span>client_side: {String(d.client_side_execute)}</span>
            )}
          </div>
          {sql ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-gray-600">
                  SQL
                </span>
                <CopyButton getText={() => sql} label="Copy SQL" />
              </div>
              <pre className="text-[11px] bg-[#0f1b2a] text-gray-100 p-2 rounded overflow-x-auto max-h-56">
                <code>{sql}</code>
              </pre>
            </div>
          ) : (
            d.input &&
            Object.keys(d.input).length > 0 && (
              <pre className="text-[11px] bg-gray-50 p-2 rounded overflow-x-auto text-gray-600 max-h-40">
                <code>{prettyJSON(d.input)}</code>
              </pre>
            )
          )}
        </SectionShell>
      );
    }

    case "sql_exec": {
      const det = d.details || {};
      return (
        <SectionShell
          icon={<CircleStackIcon className="h-4 w-4 text-cyan-700" />}
          title="SQL execution"
          accent="border-l-cyan-600"
          seq={ev.seq}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${sqlStateStyle(
                det.state,
                det.status
              )}`}
            >
              {det.state || det.status || "—"}
            </span>
            {det.produced_rows !== undefined && (
              <span className="text-[11px] text-gray-600">
                {det.produced_rows} rows
              </span>
            )}
            {det.scan_bytes !== undefined && (
              <span className="text-[11px] text-gray-600">
                {det.scan_bytes} bytes scanned
              </span>
            )}
          </div>
          {(d.message || det.QueryID) && (
            <div className="mt-1 text-[11px] text-gray-500">
              {det.QueryID && <div>QueryID: {det.QueryID}</div>}
              {d.message && <div>{d.message}</div>}
            </div>
          )}
        </SectionShell>
      );
    }

    case "error": {
      const err = d.content?.[0]?.json?.error || {};
      return (
        <SectionShell
          icon={<ExclamationTriangleIcon className="h-4 w-4 text-red-600" />}
          title="Error"
          accent="border-l-red-500"
          seq={ev.seq}
        >
          <div className="text-sm text-red-700 whitespace-pre-wrap break-words">
            {err.message || prettyJSON(err)}
          </div>
          {err.code !== undefined && (
            <div className="mt-1 text-[11px] text-red-500">code: {err.code}</div>
          )}
        </SectionShell>
      );
    }

    case "tool_result": {
      const json = d.content?.[0]?.json;
      const resultSet = json?.result_set;
      return (
        <SectionShell
          icon={<TableCellsIcon className="h-4 w-4 text-emerald-600" />}
          title="Tool result"
          accent="border-l-emerald-500"
          seq={ev.seq}
        >
          {resultSet ? (
            <ResultSetView resultSet={resultSet} />
          ) : (
            <pre className="text-[11px] bg-gray-50 p-2 rounded overflow-x-auto text-gray-600 max-h-56">
              <code>{prettyJSON(json ?? d.content)}</code>
            </pre>
          )}
        </SectionShell>
      );
    }

    case "final":
      return (
        <SectionShell
          icon={<CheckIcon className="h-4 w-4 text-green-600" />}
          title="Final answer formed"
          accent="border-l-green-500"
          seq={ev.seq}
        >
          <div className="text-[11px] text-gray-500">
            The answer shown above was produced from the steps in this trace.
          </div>
        </SectionShell>
      );

    default:
      return (
        <SectionShell
          icon={<span className="h-2 w-2 rounded-full inline-block bg-gray-400" />}
          title={ev.event || "Event"}
          accent="border-l-gray-300"
          seq={ev.seq}
        >
          <pre className="text-[11px] bg-gray-50 p-2 rounded overflow-x-auto text-gray-600 max-h-40">
            <code>{prettyJSON(d)}</code>
          </pre>
        </SectionShell>
      );
  }
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface ReasoningTraceModalProps {
  isOpen: boolean;
  onClose: () => void;
  thinkingText?: string;
  trace?: TraceEvent[];
}

const ReasoningTraceModal: React.FC<ReasoningTraceModalProps> = ({
  isOpen,
  onClose,
  thinkingText,
  trace,
}) => {
  const [showRaw, setShowRaw] = useState(false);

  // Stable chronological order: by sequence number, falling back to original
  // index to keep equal-seq events in arrival order.
  const ordered = useMemo(() => {
    const list = (trace || []).map((ev, idx) => ({ ev, idx }));
    list.sort((a, b) => (a.ev.seq - b.ev.seq) || (a.idx - b.idx));
    return list.map((x) => x.ev);
  }, [trace]);

  // Reasoning deltas are summarised by the thinkingText block; request-id is
  // metadata. Drop both from the timeline to keep it readable.
  const timeline = useMemo(
    () =>
      ordered
        .map((ev) => ({ ev, kind: classifyTraceEvent(ev) }))
        .filter((x) => x.kind !== "reasoning" && x.kind !== "request_id"),
    [ordered]
  );

  const anyTruncated = (trace || []).some((ev) => ev.truncated);

  if (!isOpen) return null;

  // Render through a portal to document.body so the fixed overlay escapes the
  // chat drawer's transformed/stacking context (which would otherwise trap it).
  return createPortal(
    <div className="fixed inset-0 z-[1000000002] overflow-y-auto w-full h-full">
      <div className="flex items-center justify-center min-h-screen px-4 py-8 text-center">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-60 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />
        <div className="inline-block align-middle bg-gray-50 rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-3xl">
          {/* Header */}
          <div className="bg-[#0f3557] px-5 py-3 flex items-center justify-between">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <LightBulbIcon className="h-5 w-5" />
              How this answer was produced
            </h3>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 max-h-[75vh] overflow-y-auto space-y-3">
            {/* Reasoning narrative */}
            {thinkingText && (
              <div className="bg-white rounded-md shadow-sm p-3 border-l-4 border-l-[#0f3557]">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                  Reasoning
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                  {thinkingText}
                </div>
              </div>
            )}

            {/* Timeline */}
            {timeline.length > 0 ? (
              timeline.map((x, i) => (
                <TraceEntry key={`${x.ev.seq}-${i}`} ev={x.ev} kind={x.kind} />
              ))
            ) : (
              !thinkingText && (
                <div className="text-sm text-gray-500 text-center py-6">
                  No detailed trace was captured for this answer.
                </div>
              )
            )}

            {anyTruncated && (
              <div className="text-[11px] text-amber-600 text-center">
                Some large payloads were truncated for display.
              </div>
            )}

            {/* Raw events fallback */}
            {(trace || []).length > 0 && (
              <div className="pt-1">
                <button
                  onClick={() => setShowRaw((v) => !v)}
                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                >
                  {showRaw ? "Hide raw events" : `Show raw events (${trace!.length})`}
                </button>
                {showRaw && (
                  <pre className="mt-2 text-[11px] bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto max-h-72">
                    <code>{prettyJSON(trace)}</code>
                  </pre>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-white px-5 py-3 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#0f3557] text-sm font-medium text-white hover:bg-[#0a2338] focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReasoningTraceModal;
